import { FlexCol, FlexRow } from '@/components/common/Flex';
import { XClose } from '@/components/common/icons';
import successAnimation from '@/components/common/successLottie.json';
import { Text } from '@/components/common/Typography';
import { CircularProgress, Fade } from '@chakra-ui/react';
import { useTransactionImportStatusProps } from '@common/react/cashflow';
import errorCircle from '/public/images/error-circle-hazard.svg';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import useSessionStorageState from 'use-session-storage-state';
import { IconButton } from '../common/Button';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

export const TransactionImportStatus = () => {
    const { transactionSyncStatus, visible } =
        useTransactionImportStatusProps();
    const [dismissedByUser, setDismissedByUser] = useSessionStorageState<
        boolean | undefined
    >('transaction-import-status-dismissed');

    const getStatusIcon = () => {
        if (!transactionSyncStatus) return;

        if (
            transactionSyncStatus === 'fetching' ||
            transactionSyncStatus === 'categorizing' ||
            transactionSyncStatus === 'waiting-for-webhook'
        ) {
            return (
                <CircularProgress
                    value={
                        {
                            'waiting-for-webhook': 25,
                            fetching: 50,
                            categorizing: 75,
                        }[transactionSyncStatus]
                    }
                    color="blue.500"
                    thickness="6px"
                    size="36px"
                />
            );
        }

        if (transactionSyncStatus === 'synced') {
            return (
                <FlexCol
                    width="36px"
                    height="36px"
                    align="center"
                    justify="center"
                >
                    <Lottie
                        animationData={successAnimation}
                        loop={false}
                        style={{ height: '36px', width: '36px' }}
                    />
                </FlexCol>
            );
        }

        if (transactionSyncStatus === 'failed') {
            return (
                <Image
                    src={errorCircle}
                    alt="error"
                    style={{ height: '32px' }}
                />
            );
        }
    };

    const getStatusText = () => {
        if (!transactionSyncStatus) return '';

        switch (transactionSyncStatus) {
            case 'fetching':
                return 'Fetching from provider...';
            case 'categorizing':
                return 'Categorizing transactions...';
            case 'failed':
                return 'Import failed';
            case 'synced':
                return 'Transactions imported';
            case 'waiting-for-webhook':
                return 'Waiting for provider...';
            default:
                return '';
        }
    };

    return (
        <Fade in={visible && !dismissedByUser}>
            <FlexCol
                position="fixed"
                mt={4}
                bottom={70}
                right={4}
                p={4}
                bg="white"
                rounded="md"
                shadow="sm"
                w={300}
                zIndex={1200}
            >
                <IconButton
                    aria-label="Close notification"
                    icon={<XClose size="sm" color="gray.500" />}
                    size="xs"
                    variant="ghost"
                    position="absolute"
                    top={0}
                    right={1}
                    onClick={() => setDismissedByUser(true)}
                    onClickEvent="Close Transaction Import Status Button Clicked"
                />
                <FlexRow align="center" gap={4} mt={2}>
                    {getStatusIcon()}
                    <FlexCol>
                        <Text>Importing transactions</Text>
                        {transactionSyncStatus && (
                            <Text color="textMuted">{getStatusText()}</Text>
                        )}
                    </FlexCol>
                </FlexRow>
            </FlexCol>
        </Fade>
    );
};
